import React, { useState } from 'react';
import connectToI18n from 'modules/i18n/connectToI18n';
import CollapsibleDetails from 'components/CollapsibleDetails.jsx';
import { TextInputGroup } from 'components/ui/form';
import { Box, BoxSection, Grid, Heading, Wrapper, Button } from 'components/ui';
import useI18n from 'hooks/I18n/useI18n.js';
import { validate, validators } from 'modules/validation';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectInvoiceRequested,
    selectAnonymousForm,
    editAnonymousForm,
    setIsAnonymous,
    continueToInvoiceForm,
    redirectToPaymentPage,
    unlockForms,
} from 'modules/giftCard';
import { loadCurrentUser } from 'modules/currentUser';
import * as R from 'ramda';
import BaseLoginContainer from 'components/Auth/BaseLoginContainer.jsx';
import { grant } from 'modules/oauth';
import AppChannel from 'modules/channels/App';
import Backbone from 'backbone';

const BuyGiftCardLogin = () => {
    const { trans } = useI18n();
    const [clientErrors, setClientErrors] = useState({});
    const [loginPanelOpened, setLoginPanelOpened] = useState(true);
    const invoiceRequested = useSelector(selectInvoiceRequested);
    const anonymousForm = useSelector(selectAnonymousForm);
    const dispatch = useDispatch();

    const handleToggleLoginPanel = () => setLoginPanelOpened(R.not);

    const handleSubmitLogin = ({ email, password, remember }) => {
        grant('password', {
            username: email,
            password,
            remember,
        })
            .done(() => {
                dispatch(
                    loadCurrentUser({
                        redirectUrl: 'giftcards',
                    })
                ).then(() => {
                    if (invoiceRequested) {
                        dispatch(continueToInvoiceForm());
                    } else {
                        dispatch(redirectToPaymentPage());
                    }
                });
            })
            .fail(error => {
                // This is listened in BaseLoginContainer
                AppChannel.vent.trigger('auth:error', error);
            });
    };

    const validateAnonymousForm = formData => {
        const { assertNotBlank, assertValidEmail } = validators(trans);
        const result = validate.all([
            validate.prop('firstName', assertNotBlank),
            validate.prop('lastName', assertNotBlank),
            validate.prop('email', assertNotBlank),
            validate.prop('email', assertValidEmail),
        ])(formData);

        const [, clientErrorsResult] = result;

        return clientErrorsResult;
    };

    const handleChange = (name, value) => {
        setClientErrors({});
        dispatch(editAnonymousForm(name, value));
    };

    const handleAnonymousSubmit = () => {
        const clientErrorsResult = validateAnonymousForm(anonymousForm);

        if (!R.isEmpty(clientErrorsResult)) {
            return setClientErrors(clientErrorsResult);
        }
        dispatch(setIsAnonymous(true));

        if (invoiceRequested) {
            return dispatch(continueToInvoiceForm());
        }

        return dispatch(redirectToPaymentPage());
    };

    const handleClickBackButton = () => {
        dispatch(unlockForms());
        Backbone.history.navigate('giftcards', { trigger: true });
    };

    return (
        <Wrapper>
            <CollapsibleDetails
                className="u-mb-4"
                isOpen={loginPanelOpened}
                onToggle={handleToggleLoginPanel}
                summary={
                    <Box>
                        <Heading size={3}>{trans('login.Ilogin')}</Heading>
                    </Box>
                }
            >
                <Box>
                    <BaseLoginContainer handleSubmit={handleSubmitLogin} showRememberMe embedded />
                </Box>
            </CollapsibleDetails>

            <CollapsibleDetails
                isOpen={!loginPanelOpened}
                onToggle={handleToggleLoginPanel}
                summary={
                    <Box>
                        <Heading size={3}>{trans('buyGiftCard.anonymous')}</Heading>
                    </Box>
                }
            >
                <Box>
                    <BoxSection>
                        <Grid sm="6|6|12|12">
                            <TextInputGroup
                                clientErrors={clientErrors.firstName}
                                label={trans('user.firstname')}
                                name="firstName"
                                inputVariant="inverse"
                                onChange={handleChange}
                                showErrors={!R.isEmpty(clientErrors.firstName)}
                                value={anonymousForm.firstName}
                            />
                            <TextInputGroup
                                clientErrors={clientErrors.lastName}
                                label={trans('user.lastname')}
                                name="lastName"
                                inputVariant="inverse"
                                onChange={handleChange}
                                showErrors={!R.isEmpty(clientErrors.lastName)}
                                value={anonymousForm.lastName}
                            />
                            <TextInputGroup
                                clientErrors={clientErrors.email}
                                label={trans('login.email')}
                                name="email"
                                inputVariant="inverse"
                                onChange={handleChange}
                                showErrors={!R.isEmpty(clientErrors.email)}
                                value={anonymousForm.email}
                            />
                            <Button
                                block
                                disabled={false}
                                onClick={handleAnonymousSubmit}
                                variant="success"
                            >
                                {trans('global.continue')}
                            </Button>
                        </Grid>
                    </BoxSection>
                </Box>
            </CollapsibleDetails>
            <Button icon="angle-left" weight="link" onClick={handleClickBackButton}>
                {trans('global.back')}
            </Button>
        </Wrapper>
    );
};

export default connectToI18n(BuyGiftCardLogin);
